import plural from 'system/libs/plural';

const INFRA_ERROR_FIELD_NAMES = [
    'NeutronError',
    'forbidden',
    'overLimit',
    'badRequest',
];

function formatErrors(errors) {
    return errors.map((error) => {
        if (typeof error !== 'string') {
            let type = error.error.toLowerCase();

            if (
                (type === 'argument_required' || type === 'argument_invalid') &&
                error.key
            ) {
                type += '/' + error.key.toLowerCase();
            }

            return type;
        }
        return error;
    });
}

/**
 * @param {InfraResponceBody[]} errors
 */
function formatInfraErrors(errors) {
    // eslint-disable-next-line sonarjs/cognitive-complexity
    return errors.map((error) => {
        if (typeof error !== 'string') {
            let type = 'default';

            const errorType = error.NeutronError && error.NeutronError.type;
            if (errorType === 'PolicyNotAuthorized') {
                return 'access';
            }

            const fieldName = INFRA_ERROR_FIELD_NAMES.find(
                (name) => error[name] && error[name].message,
            );
            const message = fieldName
                ? error[fieldName].message
                : error.message || '';

            // Example: Quota exceeded for resources: ['floatingip'].
            const arrayOfResourcesRegex = /(?:[\s\S]*Quota exceeded for resources: )(\[[\w'",\s]+])(?:[\s\S]*)/;

            // Example: Quota exceeded for cores, ram: Requested 2, 4096, but already used 1, 1024 of 2, 4096 cores, ram
            const notArrListOfResourcesRegex = /(?:[\s\S]*Quota exceeded for )(.+)(?::.+)/;

            // Example: The number of defined ports: 51 is over the limit: 50
            const oneResourceRegex = /[\s\S]*The number of defined ([A-z]+):[\s\S]*is over the limit: [\s\S]*/;

            const otherRegexes = [
                {
                    regex: /Timed out/i,
                    type: 'timeout',
                },
                {
                    regex: /VolumeLimitExceeded/,
                    type: 'quota/volumes',
                },
                {
                    regex: /SnapshotLimitExceeded/,
                    type: 'quota/snapshots',
                },
                {
                    regex: /VolumeSizeExceedsAvailableQuota/,
                    type: 'quota/volumesize',
                },
                {
                    regex: /Maximum number of ports exceeded/,
                    type: 'quota/ports',
                },
                {
                    regex: /Quota exceeded, too many key pairs/,
                    type: 'quota/keypairs',
                },
                {
                    regex: /[\s\S]*No More Ip Addresses Available On Network[\s\S]*/,
                    type: 'quota/subnetIps',
                },
                {
                    regex: /[\s\S]*CREATE aborted[\s\S]*Timed out[\s\S]*/i,
                    type: 'timeout',
                },
                {
                    regex: /[\s\S]*Failed to create certificates[\s\S]*/i,
                    type: 'certificates',
                },
                {
                    regex: /[\s\S]*DELETE[\s\S]*failed[\s\S]*Conflict[\s\S]*/i,
                    type: 'delete/conflict',
                },
                {
                    regex: /[\s\S]*DELETE[\s\S]*failed[\s\S]*Conflict[\s\S]*private_subnet[\s\S]*/i,
                    type: 'delete/conflict/subnet',
                },
                {
                    regex: /[\s\S]*UPDATE[\s\S]*failed[\s\S]*resources[\s\S]*/i,
                    type: 'update/conflict',
                },
                {
                    regex: /[\s\S]*Invalid volume[\s\S]*Volume to be backed up must be available or in-use[\s\S]*/i,
                    type: 'volume/inuse',
                },
                {
                    regex: /[\s\S]*BackupLimitExceeded[\s\S]*Maximum number of backups allowed[\s\S]*exceeded[\s\S]*/i,
                    type: 'quota/backups',
                },
                {
                    regex: /[\s\S]*External network[\s\S]*is not reachable from subnet[\s\S]*Therefore, cannot associate Port[\s\S]* with a Floating IP[\s\S]*/i,
                    type: 'external/notreachable',
                },
                {
                    regex: /TLS container h could not be found*/i,
                    type: 'loadbalancer/notedit/listener',
                },
                {
                    regex: /Invalid input for loadbalancer_id*/i,
                    type: 'loadbalancer/id/listener',
                },
                {
                    regex: /Load Balancer [\d\w-]+ already has a listener with protocol_port of [\d]+$/,
                    type: 'loadbalancer/protocol_port',
                },
                {
                    regex: /Invalid state PENDING_UPDATE of loadbalancer resource [\d\w-]+$/,
                    type: 'loadbalancer/update',
                },
                {
                    regex: /Invalid input for operation: Requested subnet with cidr: [\d\w-./]+ for network: [\d\w-]+ overlaps with another subnet.$/,
                    type: 'network/cidr',
                },
                {
                    regex: /Invalid input for cidr. Reason: '[\d\w-./]+' isn't a recognized IP subnet cidr, '[\d\w-./]+' is recommended.$/,
                    type: 'network/invalid/cidr',
                },
                {
                    regex: /Invalid input for cidr. Reason: '[\d\w-./]+' is not a valid IP subnet.$/,
                    type: 'network/invalid/cidr',
                },
                {
                    regex: /Invalid input for gateway_ip. Reason: '[\d\w-./]+' is not a valid IP address.$/,
                    type: 'network/invalid/gateway',
                },
                {
                    regex: /Invalid input for dns_nameservers. Reason: '[\d\w-./А-Яа-яёЁ]+' is not a valid nameserver. '[\d\w-./А-Яа-яёЁ]+' is not a valid IP address.$/,
                    type: 'network/invalid/dns',
                },
                {
                    regex: /Invalid input for host_routes. Reason: Invalid input. '[\d\w-./А-Яа-яёЁ]+' must be a dictionary with keys: [\d\w-./А-Яа-яёЁ'' , [\]]+.$/,
                    type: 'network/invalid/host',
                },
                {
                    regex: /Request Failed: internal server error while processing your request.$/,
                    type: 'network/router',
                },
                {
                    regex: /No more IP addresses available on network [\d\w-]+. Neutron server returns request_ids: ['[\d\w-]+']$/,
                    type: 'loadbalancer/subnets',
                },
                {
                    regex: /No more IP addresses available on network [\d\w-]+.$/,
                    type: 'loadbalancer/subnets/intNet',
                },
                {
                    regex: /Share access .+ exists./,
                    type: 'share/access/exists',
                },
            ];

            switch (true) {
                case arrayOfResourcesRegex.test(message):
                    try {
                        const resources = JSON.parse(
                            message
                                .replace(arrayOfResourcesRegex, '$1')
                                .replace(/'/g, '"'),
                        );
                        type = 'quota/' + resources[0];
                    } catch (e) {
                        type = 'quota';
                    }
                    break;
                case notArrListOfResourcesRegex.test(message): {
                    const resources = message
                        .replace(notArrListOfResourcesRegex, '$1')
                        .split(', ');
                    type = `quota/${resources[0]}`;
                    break;
                }
                case oneResourceRegex.test(message): {
                    const resource = message.replace(oneResourceRegex, '$1');
                    type = `quota/${resource}`;
                    break;
                }
                default:
                    otherRegexes.some(({ regex, type: errorType }) => {
                        if (regex.test(message)) {
                            type = errorType;
                            return true;
                        }
                    });
                    break;
            }
            return type;
        }
        return error;
    });
}

function calculateErrorTypes(errors, texts) {
    const errorTypes = {};

    errors.forEach((error) => {
        let type;
        const path = error.split('/');
        const errors = [];

        path.forEach((e, i) => {
            'use strict';

            errors.push(path.slice(0, i + 1).join('/'));
        });

        errors.reverse();

        errors.forEach((error) => {
            'use strict';

            if (!type && texts[error]) {
                type = error;
            }
        });

        if (!type) {
            type = 'default';
        }

        if (!errorTypes[type]) {
            errorTypes[type] = 0;
        }

        errorTypes[type]++;
    });

    return errorTypes;
}

function formatTexts(errors) {
    const formattedTexts = {};
    Object.keys(errors).forEach((error) => {
        const texts = errors[error];

        if (typeof texts === 'string') {
            formattedTexts[error] = [texts, texts, texts];
        } else if (texts.length === 2) {
            formattedTexts[error] = [...texts, texts[1]];
        }
    });

    return formattedTexts;
}

function errorsCountFromTypes(types) {
    let count = 0;

    Object.keys(types).forEach((value) => {
        count += value;
    });

    return count;
}

export const Errors = {
    /**
     * @typedef {Object} ResponceBody
     * @property {string} error
     * @property {string} [field]
     */

    /**
     * @typedef {Object} Responce
     * @property {(ResponceBody|ResponceBody[])} body
     */

    /**
     * @param {Responce} responce
     */
    getErrorsFromResponse(response) {
        return this.getErrorsFromResponses(response);
    },

    /**
     * @param {(Responce|Responce[])} responses
     */
    getErrorsFromResponses(responses) {
        let errors = [];

        responses = [].concat(responses);

        responses.forEach((res) => {
            if (
                res.body &&
                (res.body.error || (res.body[0] && res.body[0].error))
            ) {
                errors = errors.concat(res.body);
            } else {
                errors.push('default');
            }
        });

        errors = formatErrors(errors);

        return errors;
    },

    /**
     * @typedef {Object} InfraError
     * @property {string} message
     */

    /**
     * @typedef {Object} InfraResponceBody
     * @property {InfraError} [NeutronError]
     * @property {InfraError} [forbidden]
     */

    /**
     * @typedef {Object} InfraResponce
     * @property {InfraResponceBody} body
     */

    /**
     * @param {InfraResponce|InfraResponceBody} responce
     */
    getErrorsFromInfraResponse(response) {
        return this.getErrorsFromInfraResponses(response);
    },

    /**
     * @param {(InfraResponce|InfraResponce[]|InfraResponceBody|InfraResponceBody[])} responses
     */
    getErrorsFromInfraResponses(responses) {
        let errors = [];

        responses = [].concat(responses);

        responses.forEach((res) => {
            if (
                typeof res.body === 'object' &&
                INFRA_ERROR_FIELD_NAMES.some(
                    (name) =>
                        res.body[name] || (res.body[0] && res.body[0][name]),
                )
            ) {
                errors = errors.concat(res.body);
            } else if (
                INFRA_ERROR_FIELD_NAMES.some((name) => res[name]) ||
                res.message
            ) {
                errors = errors.concat(res);
            } else {
                errors.push('default');
            }
        });

        errors = formatInfraErrors(errors);

        return errors;
    },

    getErrorsFromAction(action) {
        return action.error && this.getErrorsFromResponses(action.error);
    },

    _doErrorsTextReplaces(errors, replaces) {
        const replacer = function(error) {
            keys.forEach((key) => {
                error = error.replace(key, replaces[key]);
            });

            return error;
        };
        const keys = replaces && Object.keys(replaces);

        if (!keys || !keys.length) {
            return errors;
        }
        if (Array.isArray(errors)) {
            return errors.map(replacer);
        }
        return replacer(errors);
    },

    getInfraErrorMessage(errors, texts, replaces) {
        return this.getErrorMessage(errors, texts, replaces, true);
    },

    getErrorMessage(errors, texts, replaces, isInfra) {
        if (isInfra) {
            errors = formatInfraErrors(errors);
        } else {
            errors = formatErrors(errors);
        }

        texts = formatTexts(texts);

        const errorTypes = calculateErrorTypes(errors, texts);
        let message;
        const types = Object.keys(errorTypes);

        if (types.length === 1) {
            message = types[0];
        } else {
            message = 'default';

            errorTypes.default = errorsCountFromTypes(errorTypes);
        }

        return this._doErrorsTextReplaces(
            plural(errorTypes[message], texts[message]),
            replaces,
        );
    },

    getInfraErrorMessages(errors, texts, replaces) {
        return this.getErrorMessage(errors, texts, replaces, true);
    },

    getErrorMessages(errors, texts, replaces, isInfra) {
        if (isInfra) {
            errors = formatInfraErrors(errors);
        } else {
            errors = formatErrors(errors);
        }

        texts = formatTexts(texts);

        const errorTypes = calculateErrorTypes(errors, texts);
        const types = Object.keys(errorTypes);

        if (types.length > 1 && errorTypes.default) {
            delete errorTypes.default;
        }

        return this._doErrorsTextReplaces(
            Object.keys(errorTypes).map((type) => {
                return plural(errorTypes[type], texts[type]);
            }),
            replaces,
        );
    },

    toBFormErrors(errors) {
        // this func put to field error only text after ARGUMENT_
        // should not to use it in new code

        errors = [].concat(errors);

        return errors.reduce((bFormErrors, reason) => {
            const name = (reason && (reason.field || reason.key)) || 'unknown';
            const type = (reason && reason.error) || 'unknown';

            if (type.indexOf('ARGUMENTS/') === 0) {
                // ARGUMENTS/INVALID/TOO_LONG → invalid
                bFormErrors[name] = type
                    .slice(10)

                    .replace(/\/.*/, '')
                    .toLowerCase();
            } else if (type.indexOf('ARGUMENT_') === 0) {
                bFormErrors[name] = type.slice(9).toLowerCase();
            } else if (type && type.length > 0) {
                bFormErrors[name] = type.toLowerCase();
            } else {
                bFormErrors[name] = 'invalid';
            }

            return bFormErrors;
        }, {});
    },
};
